import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n.jsx";
import { PageHeader } from "../components/Bits";
import TransInput from "../components/TransInput";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Copy, Shield, Lock } from "lucide-react";

const SCOPES = ["self", "team", "subordinates", "projects", "department", "site", "all"];

export default function Roles() {
  const { t, tn } = useI18n();
  const [roles, setRoles] = useState([]);
  const [perms, setPerms] = useState([]);
  const [sel, setSel] = useState(null);
  const [modal, setModal] = useState(false);
  const [name, setName] = useState({});

  const load = () => api.get("/roles").then((r) => setRoles(r.data));
  useEffect(() => { load(); api.get("/permissions").then((r) => setPerms(r.data)); }, []);

  const pick = (role) => setSel(JSON.parse(JSON.stringify(role)));
  const permScope = (perm) => sel?.permissions?.find((p) => p.permission === perm)?.scope;
  const toggle = (perm, checked) => {
    let list = sel.permissions.filter((p) => p.permission !== perm);
    if (checked) list.push({ permission: perm, scope: "all" });
    setSel({ ...sel, permissions: list });
  };
  const setScope = (perm, scope) => {
    setSel({ ...sel, permissions: sel.permissions.map((p) => p.permission === perm ? { ...p, scope } : p) });
  };
  const createRole = async () => {
    try { const { data } = await api.post("/roles", { name, permissions: [] }); toast.success(t("save")); setModal(false); setName({}); load(); pick(data); }
    catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  const copyRole = async (r) => { const { data } = await api.post(`/roles/${r.id}/copy`, { name: { ...r.name, en: (r.name.en || "") + " copy" } }); load(); pick(data); toast.success(t("copied")); };

  const grouped = perms.reduce((a, p) => { (a[p.group] = a[p.group] || []).push(p); return a; }, {});
  const [tab, setTab] = useState("perms");
  const [newPerm, setNewPerm] = useState("");
  const NAV_KEYS = ["dashboard", "start_work", "my_reports", "employees", "projects", "reports", "worktime", "pdf_reports", "analytics", "plan_fact", "works", "warehouse", "material_requests", "norms", "documents", "roles", "positions", "units", "organization", "onboarding", "forms", "workflows", "automations", "audit", "theme", "settings"];
  const WIDGETS = ["employees", "projects", "actual_hours", "efficiency", "reports", "approved", "norm_hours", "cost", "chart_efficiency", "chart_reports"];
  const toggleList = (key, item) => {
    const list = sel[key] || [];
    setSel({ ...sel, [key]: list.includes(item) ? list.filter((x) => x !== item) : [...list, item] });
  };
  const saveRole = async () => {
    try { await api.put(`/roles/${sel.id}`, { permissions: sel.permissions, name: sel.name, nav: sel.nav || [], dashboard_widgets: sel.dashboard_widgets || [] }); toast.success(t("save")); load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };
  const addPerm = async () => {
    if (!newPerm.includes(".")) return toast.error("resource.action");
    try { await api.post("/permissions", { key: newPerm.trim(), group: "Custom" }); setNewPerm(""); api.get("/permissions").then((r) => setPerms(r.data)); toast.success(t("save")); }
    catch (e) { toast.error(e.response?.data?.detail || t("error")); }
  };

  return (
    <div>
      <PageHeader title={t("roles")} subtitle={t("rbac_scope")}
        action={<Button data-testid="add-role-btn" onClick={() => setModal(true)} className="bg-[var(--amber)] hover:bg-[var(--amber-2)] text-white gap-2"><Plus size={16} /> {t("create")}</Button>} />
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="tactical-card p-2 h-fit">
          {roles.map((r) => (
            <button key={r.id} data-testid={`role-${r.key}`} onClick={() => pick(r)}
              className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-left text-sm ${sel?.id === r.id ? "bg-[var(--amber)] text-white" : "hover:bg-[var(--bg)]"}`}>
              <Shield size={15} /> <span className="flex-1 font-semibold">{tn(r.name)}</span>
              {r.is_system && <Lock size={12} className="opacity-60" />}
              <Copy size={13} onClick={(e) => { e.stopPropagation(); copyRole(r); }} data-testid={`copy-role-${r.key}`} />
            </button>
          ))}
        </div>
        <div className="lg:col-span-2 tactical-card p-4">
          {!sel ? <div className="text-[var(--text-3)] text-center py-10">{t("roles")} →</div> : (
            <>
              <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
                <h3 className="font-display font-bold text-lg">{tn(sel.name)}</h3>
                <div className="flex gap-1">
                  <button onClick={() => setTab("perms")} data-testid="tab-perms" className={`px-3 h-8 rounded-md text-xs font-semibold ${tab === "perms" ? "bg-[var(--sidebar)] text-white" : "tactical-card"}`}>{t("permissions")}</button>
                  <button onClick={() => setTab("nav")} data-testid="tab-nav" className={`px-3 h-8 rounded-md text-xs font-semibold ${tab === "nav" ? "bg-[var(--sidebar)] text-white" : "tactical-card"}`}>{t("navigation")}</button>
                  <Button size="sm" onClick={saveRole} className="bg-[var(--green)] text-white" data-testid="save-role">{t("save")}</Button>
                </div>
              </div>
              {tab === "nav" && (
                <div className="space-y-4" data-testid="nav-config">
                  <p className="text-xs text-[var(--text-3)]">{t("nav_hint")}</p>
                  <div>
                    <div className="text-xs uppercase font-bold text-[var(--text-3)] mb-1">{t("navigation")}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                      {NAV_KEYS.map((k) => <label key={k} className="flex items-center gap-2 text-sm tactical-card px-2 py-1.5"><Checkbox checked={(sel.nav || []).includes(k)} onCheckedChange={() => toggleList("nav", k)} data-testid={`nav-${k}`} /> {t(k)}</label>)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs uppercase font-bold text-[var(--text-3)] mb-1">{t("widgets")}</div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                      {WIDGETS.map((k) => <label key={k} className="flex items-center gap-2 text-sm tactical-card px-2 py-1.5"><Checkbox checked={(sel.dashboard_widgets || []).includes(k)} onCheckedChange={() => toggleList("dashboard_widgets", k)} data-testid={`widget-${k}`} /> {t(k.replace("chart_", ""))}{k.startsWith("chart_") ? " 📊" : ""}</label>)}
                    </div>
                  </div>
                </div>
              )}
              {tab === "perms" && <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
                <div className="flex gap-2">
                  <input value={newPerm} onChange={(e) => setNewPerm(e.target.value)} placeholder={t("new_permission")} className="border rounded-md h-9 px-2 text-sm flex-1 bg-[var(--card)]" data-testid="new-perm-input" />
                  <Button size="sm" variant="outline" onClick={addPerm} data-testid="new-perm-add"><Plus size={14} /></Button>
                </div>
                {Object.entries(grouped).map(([group, list]) => (
                  <div key={group}>
                    <div className="text-xs uppercase font-bold text-[var(--text-3)] mb-1">{group}</div>
                    <div className="space-y-1">
                      {list.map((p) => {
                        const active = permScope(p.key) !== undefined;
                        return (
                          <div key={p.key} className="flex items-center gap-2 text-sm">
                            <Checkbox checked={active} onCheckedChange={(c) => toggle(p.key, c)} data-testid={`perm-${p.key}`} />
                            <span className="flex-1 font-mono-num text-xs">{p.key}</span>
                            {active && (
                              <select value={permScope(p.key)} onChange={(e) => setScope(p.key, e.target.value)}
                                className="border rounded px-1 py-0.5 text-xs" data-testid={`scope-${p.key}`}>
                                {SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
                              </select>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>}
            </>
          )}
        </div>
      </div>
      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("create")} — {t("role")}</DialogTitle></DialogHeader>
          <TransInput value={name} onChange={setName} placeholder={t("name")} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setModal(false)}>{t("cancel")}</Button>
            <Button onClick={createRole} className="bg-[var(--amber)] text-white" data-testid="create-role-save">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
